import React, { useState, useRef } from 'react';
import { Button, Icon, Image, Modal, Header, Segment } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { authUtil } from '../../../utils';
// import { useHostAppProxy } from '../../../contexts/HostAppProxyContext';

interface ProfilePhotoUploadProps {
  currentPhotoUrl?: string;
  onPhotoUpdate: (photoUrl: string) => void;
  size?: number;
}

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  currentPhotoUrl,
  onPhotoUpdate,
  size = 150,
}) => {
  const { t } = useTranslation();
  // const { settings } = useHostAppProxy();
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if running in native app (disabled for now)
  // const isInNativeApp = Boolean(
  //   window.ReactNativeWebView // || settings.isWrapped
  // );

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
      toast.error('Please select a valid image file (JPEG, JPG, or PNG)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setSelectedFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = e => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    setShowModal(true);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const idToken = await authUtil.getFreshIdToken();
      const formData = new FormData();
      formData.append('photo', selectedFile);

      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/v2/profile/photo`,
        {
          method: 'POST',
          headers: {
            'br-token': idToken,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.msg || 'Failed to upload photo');
      }

      const result = await response.json();
      onPhotoUpdate(result.photoUrl);
      toast.success('Profile photo updated successfully!');
      setShowModal(false);
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (error: any) {
      console.error('Photo upload error:', error);
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        {currentPhotoUrl ? (
          <Image
            src={currentPhotoUrl}
            size={size > 100 ? 'medium' : 'small'}
            circular
            style={{
              width: size,
              height: size,
              objectFit: 'cover',
              cursor: 'pointer',
            }}
            onClick={triggerFileSelect}
          />
        ) : (
          <div
            onClick={triggerFileSelect}
            style={{
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: 'var(--br-raised-surface)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: '2px dashed var(--br-border)',
            }}
          >
            <Icon name="camera" size="large" color="grey" />
          </div>
        )}
        <Button
          circular
          icon="camera"
          size="mini"
          color="blue"
          onClick={triggerFileSelect}
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            zIndex: 10,
          }}
        />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <Modal size="small" open={showModal} onClose={handleCancel}>
        <Modal.Header>
          <Icon name="camera" />
          {t('UpdateProfilePhoto') || 'Update Profile Photo'}
        </Modal.Header>
        <Modal.Content>
          {previewUrl && (
            <Segment textAlign="center">
              <Image
                src={previewUrl}
                size="medium"
                circular
                centered
                style={{
                  maxWidth: '200px',
                  maxHeight: '200px',
                  objectFit: 'cover',
                }}
              />
            </Segment>
          )}
          <p>
            Are you sure you want to upload this photo as your profile picture?
          </p>
          <p>
            <strong>Note:</strong> The image will be publicly visible to other
            users.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCancel}>{t('Cancel') || 'Cancel'}</Button>
          <Button
            primary
            loading={uploading}
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            <Icon name="upload" />
            {uploading ? 'Uploading...' : t('Upload') || 'Upload'}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default ProfilePhotoUpload;
